import "./HsaFsaPaymentFlow.scss";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import WithFlexContainer from "@components/PaymentMethodManager/components/PaymentForm/WithFlexContainer";
import { AlertBanner, AlertBannerMessage, Button } from "@runwayhealth/runway-components-react";
import * as casesActions from "@store/cases/casesActions";
import { ErrorEvents, RootState } from "@types-runway/state";
import {
  PAYMENT_PROVIDER_WITHFLEX,
  VISIT_FEE_WITHFLEX_PRICE_ID,
  VISIT_FEE_WITHFLEX_PRODUCT_ID,
} from "@utils/constantsEnv";

interface HsaFsaPaymentFlowProps {
  caseId: number;
  onClose: () => void;
  promoCodeId?: string;
  totalAmount: number;
}

/**
 * HsaFsaPaymentFlow Component
 *
 * Smart component that handles the complete HSA/FSA payment flow using WithFlex.
 * Orchestrates invoice creation, loading states, error handling, and iframe rendering.
 *
 * Architecture:
 * - Self-contained: Handles all WithFlex-specific logic
 * - Redux-connected: Dispatches invoice creation, reads invoice state
 * - Modal-friendly: Designed to be rendered in OverlayPanel
 *
 * Flow:
 * 1. On mount: Dispatch CREATE_CASE_INVOICE (idempotent)
 * 2. Loading: Show spinner while invoice creates
 * 3. Success: Render WithFlexContainer with hostedInvoiceUrl
 * 4. Error: Show error message with retry option
 */
const HsaFsaPaymentFlow: React.FC<HsaFsaPaymentFlowProps> = ({
  caseId,
  onClose,
  promoCodeId,
  totalAmount,
}) => {
  const dispatch = useDispatch();

  // Get case invoice state from Redux
  const newCase = useSelector((state: RootState) => state.case.newCase);
  const invoice = newCase.data?.invoice;
  const isPending = newCase.isPending;
  const error = newCase.error;

  const showFullDiscountNotice = totalAmount === 0;
  const [userAcknowledged, setUserAcknowledged] = useState(false);

  // Construct redirect URLs for WithFlex
  const successUrl = `${window.location.origin}/payment-success`;
  const cancelUrl = `${window.location.origin}/checkout`;

  // Create invoice on mount if it doesn't exist
  useEffect(() => {
    createInvoice();
  }, []);

  const createInvoice = () => {
    dispatch({
      type: casesActions.CREATE_CASE_INVOICE,
      caseId,
      pricing: {
        priceId: VISIT_FEE_WITHFLEX_PRICE_ID!,
        productId: VISIT_FEE_WITHFLEX_PRODUCT_ID!,
        provider: PAYMENT_PROVIDER_WITHFLEX,
      },
      promoCodeId,
      redirects: {
        successUrl,
        cancelUrl,
      },
    });
  };

  // Error state
  if (error?.message && !isPending && error?.message === ErrorEvents.FAILED_INVOICE) {
    return (
      <div className="hsa-fsa-payment-flow hsa-fsa-payment-flow--error">
        <div className="hsa-fsa-payment-flow__error-container">
          <h2>Unable to Load Payment</h2>
          <p>We couldn't create your HSA/FSA payment. Please try again.</p>
          {error.message && <p className="error-message">{error.message}</p>}
          <div className="hsa-fsa-payment-flow__actions">
            <button className="btn btn-primary" onClick={createInvoice}>
              Retry
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full discount notice state
  // When a full discount promo code is applied, we still need to process a minimal charge
  if (showFullDiscountNotice && !userAcknowledged && !isPending && invoice?.hostedInvoiceUrl) {
    const handleAcknowledge = () => {
      setUserAcknowledged(true);
    };

    return (
      <div className="hsa-fsa-payment-flow hsa-fsa-payment-flow--discount-notice">
        <div className="hsa-fsa-payment-flow__discount-notice-container">
          <h3>Temporary Processing Fee</h3>
          <div className="discount-notice-content">
            <p className="discount-notice-main">
              Your discount has been <b>successfully applied</b>, however, the payment processor
              requires a <b>temporary charge</b> to verify your HSA/FSA account and ensure a secure
              transaction. This charge will be <b>refunded</b> after the transaction is processed.
            </p>
          </div>
          <Button size="lg" customWidth={400} onClick={handleAcknowledge}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isPending || !invoice?.hostedInvoiceUrl) {
    return (
      <div className="hsa-fsa-payment-flow hsa-fsa-payment-flow--loading">
        <div className="hsa-fsa-payment-flow__loading-container">
          <div className="spinner" />
          <h2>Preparing Your HSA/FSA Payment</h2>
          <p>Please wait while we set up your secure payment...</p>
        </div>
      </div>
    );
  }

  // Success state - render WithFlex iframe
  return (
    <div className="hsa-fsa-payment-flow hsa-fsa-payment-flow--ready">
      <WithFlexContainer src={invoice.hostedInvoiceUrl} />
    </div>
  );
};

export default HsaFsaPaymentFlow;
